import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, soccerUrl } from '../utils/paths';
import './MatchPreviewEditor.css';

const FIELD_POSITIONS = [
  'GK', 'RB', 'CB', 'LB', 'RWB', 'LWB', 'CDM', 'CM', 'CAM', 'RM', 'LM', 'RW', 'LW', 'CF', 'ST',
];

const formatPosition = (position) =>
  position ? position.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';

const MatchPreviewEditor = () => {
  const { id: clubId, matchId } = useParams();
  const navigate = useNavigate();
  const { user, isClubAdmin } = useAuth();
  const [match, setMatch] = useState(null);
  const [club, setClub] = useState(null);
  const [userMember, setUserMember] = useState(null);
  const [rosterPlayers, setRosterPlayers] = useState([]);
  const [previewNotes, setPreviewNotes] = useState('');
  const [squad, setSquad] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canManage = () => {
    if (!user) return false;
    if (isClubAdmin() && club?.adminUserId === user.id) return true;
    if (userMember?.roles?.includes('MATCH_COORDINATOR')) return true;
    if (match?.createdByUserId === user.id) return true;
    return false;
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [matchData, clubData] = await Promise.all([
        api.getMatchById(matchId),
        api.getClubById(clubId),
      ]);
      setMatch(matchData);
      setClub(clubData);
      setPreviewNotes(matchData.previewNotes || '');
      setSquad(
        (matchData.squad || []).map((player) => ({
          playerId: player.playerId,
          playerName: player.playerName,
          role: player.role || 'STARTING',
          fieldPosition: player.fieldPosition || '',
          notes: player.notes || '',
          jerseyNumber: player.jerseyNumber ?? '',
        }))
      );

      if (user) {
        const members = await api.getClubMembers(clubId, 'APPROVED', user.id);
        const member = members.find(
          (m) => m.player?.userId === user.id || m.player?.email === user.email
        );
        setUserMember(member || null);
        setRosterPlayers(
          members
            .filter((m) => m.player)
            .map((m) => m.player)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        );
      }
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load match preview');
    } finally {
      setLoading(false);
    }
  }, [clubId, matchId, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const availablePlayers = rosterPlayers.filter(
    (player) => !squad.some((s) => s.playerId === player.id)
  );

  const handleAddPlayer = () => {
    if (!selectedPlayerId) return;
    const player = rosterPlayers.find((p) => p.id === selectedPlayerId);
    if (!player) return;
    setSquad((prev) => [
      ...prev,
      {
        playerId: player.id,
        playerName: player.name,
        role: 'STARTING',
        fieldPosition: player.position ? player.position.replace(/_/g, ' ').slice(0, 3).toUpperCase() : '',
        notes: '',
        jerseyNumber: '',
      },
    ]);
    setSelectedPlayerId('');
  };

  const handleSquadChange = (index, field, value) => {
    setSquad((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleRemovePlayer = (index) => {
    setSquad((prev) => prev.filter((_, i) => i !== index));
  };

  const buildPreviewPayload = () => ({
    previewNotes,
    squad: squad.map((row) => ({
      playerId: row.playerId,
      role: row.role,
      fieldPosition: row.fieldPosition || null,
      notes: row.notes || null,
      jerseyNumber: row.jerseyNumber === '' || row.jerseyNumber == null
        ? null
        : Number(row.jerseyNumber),
    })),
  });

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updated = await api.updateMatchPreview(matchId, buildPreviewPayload(), user.id);
      setMatch(updated);
      setSuccess('Match preview saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save match preview');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async (published) => {
    if (!user?.id) return;
    setPublishing(true);
    setError('');
    setSuccess('');
    try {
      if (published) {
        await api.updateMatchPreview(matchId, buildPreviewPayload(), user.id);
      }
      const updated = await api.setMatchPublishedToPublic(matchId, user.id, published);
      if (published && !updated.previewShareToken) {
        const share = await api.ensureMatchPreviewShareToken(matchId, user.id);
        updated.previewShareToken = share.previewShareToken;
      }
      setMatch(updated);
      setSuccess(published ? 'Match preview published.' : 'Match preview unpublished.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update publish status');
    } finally {
      setPublishing(false);
    }
  };

  const ensureShareToken = async () => {
    if (match?.previewShareToken) return match.previewShareToken;
    const share = await api.ensureMatchPreviewShareToken(matchId, user.id);
    setMatch((prev) => ({ ...prev, previewShareToken: share.previewShareToken }));
    return share.previewShareToken;
  };

  const handleCopyShareLink = async () => {
    try {
      const token = await ensureShareToken();
      const url = soccerUrl(`/public/match-preview/${token}`);
      await navigator.clipboard.writeText(url);
      setSuccess('Share link copied to clipboard.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to copy share link');
    }
  };

  const handleShareWhatsApp = async () => {
    try {
      const token = await ensureShareToken();
      const url = soccerUrl(`/public/match-preview/${token}`);
      const message =
        `⚽ *${match.title}*\n\n` +
        (match.opponentName ? `vs ${match.opponentName}\n` : '') +
        `📅 ${new Date(match.matchDate).toLocaleString()}\n` +
        (match.location ? `📍 ${match.location}\n` : '') +
        `\nView match preview:\n${url}`;
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`, '_blank');
    } catch (err) {
      setError(err.message || 'Failed to share via WhatsApp');
    }
  };

  if (loading) {
    return <div className="loading">Loading match preview...</div>;
  }

  if (!match) {
    return <div className="alert alert-error">Match not found</div>;
  }

  if (!canManage()) {
    return (
      <div className="match-preview-editor">
        <div className="alert alert-error">You do not have permission to manage this match preview.</div>
        <Link to={soccerPath(`/clubs/${clubId}/matches`)} className="btn btn-secondary">
          Back to matches
        </Link>
      </div>
    );
  }

  const startingCount = squad.filter((p) => p.role === 'STARTING').length;

  return (
    <div className="match-preview-editor">
      <div className="match-preview-editor__header">
        <button
          type="button"
          onClick={() => navigate(soccerPath(`/clubs/${clubId}/matches`))}
          className="btn btn-secondary"
        >
          ← Back to matches
        </button>
        <h2>Match Preview</h2>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="match-preview-editor__meta">
        <p><strong>{match.title}</strong></p>
        {match.opponentName && <p><strong>Opponent:</strong> {match.opponentName}</p>}
        <p><strong>Date:</strong> {new Date(match.matchDate).toLocaleString()}</p>
        {match.location && <p><strong>Location:</strong> {match.location}</p>}
        {match.description && <p><strong>Description:</strong> {match.description}</p>}
      </div>

      <div
        className={`match-preview-editor__publish-banner ${
          match.publishedToPublic ? 'match-preview-editor__publish-banner--live' : ''
        }`}
      >
        <div>
          <strong>
            {match.publishedToPublic
              ? 'Published — visible on club home and share link'
              : 'Draft — not visible publicly yet'}
          </strong>
          {match.publishedToPublic && match.previewShareToken && (
            <div className="match-preview-editor__share-row">
              <Link
                to={soccerPath(`/public/match-preview/${match.previewShareToken}`)}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                View public page
              </Link>
              <button type="button" className="btn btn-secondary" onClick={handleCopyShareLink}>
                Copy share link
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ backgroundColor: '#25D366' }}
                onClick={handleShareWhatsApp}
              >
                Share on WhatsApp
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          className={`btn ${match.publishedToPublic ? 'btn-secondary' : 'btn-success'}`}
          onClick={() => handlePublishToggle(!match.publishedToPublic)}
          disabled={publishing}
        >
          {publishing
            ? 'Updating...'
            : match.publishedToPublic
              ? 'Unpublish preview'
              : 'Publish preview'}
        </button>
      </div>

      <div className="match-preview-editor__section">
        <h3>Coach preview message</h3>
        <p style={{ color: '#666', marginTop: 0 }}>
          Share context about the upcoming match — tactics, focus areas, or a message to supporters.
        </p>
        <textarea
          rows="5"
          value={previewNotes}
          onChange={(e) => setPreviewNotes(e.target.value)}
          placeholder="e.g. Big league match this weekend — come support the squad!"
          style={{ width: '100%' }}
        />
      </div>

      <div className="match-preview-editor__section">
        <h3>Squad ({startingCount} starting · {squad.length - startingCount} subs)</h3>
        <p style={{ color: '#666', marginTop: 0 }}>
          Add players from your club roster. Assign positions and optional notes for each player.
        </p>

        {squad.length === 0 && <p>No players added yet.</p>}

        <div className="match-preview-editor__squad-list">
          {squad.map((row, index) => (
            <div key={row.playerId} className="match-preview-editor__squad-row">
              <div>
                <label>Player</label>
                <strong>{row.playerName}</strong>
              </div>
              <div>
                <label>Role</label>
                <select
                  value={row.role}
                  onChange={(e) => handleSquadChange(index, 'role', e.target.value)}
                >
                  <option value="STARTING">Starting</option>
                  <option value="SUBSTITUTE">Substitute</option>
                </select>
              </div>
              <div>
                <label>Position</label>
                <select
                  value={row.fieldPosition}
                  onChange={(e) => handleSquadChange(index, 'fieldPosition', e.target.value)}
                >
                  <option value="">—</option>
                  {FIELD_POSITIONS.map((pos) => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>#</label>
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={row.jerseyNumber}
                  onChange={(e) => handleSquadChange(index, 'jerseyNumber', e.target.value)}
                />
              </div>
              <div>
                <label>Notes</label>
                <input
                  type="text"
                  value={row.notes}
                  onChange={(e) => handleSquadChange(index, 'notes', e.target.value)}
                  placeholder="Optional spotlight"
                />
              </div>
              <div>
                <label>&nbsp;</label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleRemovePlayer(index)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="match-preview-editor__add-player">
          <select value={selectedPlayerId} onChange={(e) => setSelectedPlayerId(e.target.value)}>
            <option value="">Add player from roster...</option>
            {availablePlayers.map((player) => (
              <option key={player.id} value={player.id}>
                {player.name}
                {player.position ? ` (${formatPosition(player.position)})` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddPlayer}
            disabled={!selectedPlayerId}
          >
            Add player
          </button>
        </div>
      </div>

      <div className="match-preview-editor__actions">
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save preview'}
        </button>
        {match.previewShareToken && (
          <Link
            to={soccerPath(`/public/match-preview/${match.previewShareToken}`)}
            className="btn btn-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Preview public page
          </Link>
        )}
      </div>
    </div>
  );
};

export default MatchPreviewEditor;
